import path from 'path-browserify';

import { useTenantStore } from '@stores/tenant-store';
import { getBaseUrl } from '@api/base-url';

type DomainType = string | null;

const base = ({ domain }: { domain: DomainType }): string => path.join(
  getBaseUrl({ version: 1 }),
  'tenants/',
  domain ? `${domain}/` : '',
);

const getDomain = () => useTenantStore().domain;

const agentUrls = Object.freeze({
  agentChats: ({
    domain = getDomain(),
    agentChatId = null,
  }: {
    domain?: string;
    agentChatId?: string | null;
  }): string => path.join(
    base({ domain }),
    'agents/assistant/chats/',
    agentChatId !== null ? `${agentChatId}/` : '',
  ),
  agentChatMessages: ({
    domain = getDomain(),
    agentChatId,
    agentChatMessageId = null,
  }: {
    domain?: string;
    agentChatId: string;
    agentChatMessageId?: number | null;
  }): string => path.join(
    base({ domain }),
    `agents/assistant/chats/${agentChatId}/messages/`,
    agentChatMessageId !== null ? `${agentChatMessageId}/` : '',
  ),
  agentJobs: ({
    domain = getDomain(),
    agentJobId = null,
  }: {
    domain?: string;
    agentJobId?: string | null;
  }): string => path.join(
    base({ domain }),
    'agents/assistant/jobs/',
    agentJobId !== null ? `${agentJobId}/` : '',
  ),
  cancelAgentJob: ({
    domain = getDomain(),
    agentJobId,
  }: {
    domain?: string;
    agentJobId: string;
  }): string => path.join(
    base({ domain }),
    `agents/assistant/jobs/${agentJobId}/cancel/`,
  ),
});

const assessmentUrls = Object.freeze({
  assessmentAvailability: ({
    domain = getDomain(),
  }: {
    domain?: string;
  }): string => path.join(
    base({ domain }),
    'assessment/availability/',
  ),
  assessmentProviders: ({
    domain = getDomain(),
    assessmentProviderId = null,
  }: {
    domain?: string;
    assessmentProviderId?: number | null;
  }): string => path.join(
    base({ domain }),
    'assessment-providers/',
    assessmentProviderId !== null ? `${assessmentProviderId}/` : '',
  ),
  assessments: ({
    domain = getDomain(),
    assessmentId = null,
  }: {
    domain?: string;
    assessmentId?: number | null;
  }): string => path.join(
    base({ domain }),
    'assessments/',
    assessmentId !== null ? `${assessmentId}/` : '',
  ),
  assessmentSkillScoreImports: ({
    domain = getDomain(),
    assessmentSkillScoreImportId = null,
  }: {
    domain?: string;
    assessmentSkillScoreImportId?: number | null;
  }): string => path.join(
    base({ domain }),
    'assessments/all/skills/all/scores/all/imports/',
    assessmentSkillScoreImportId !== null ? `${assessmentSkillScoreImportId}/` : '',
  ),
  assessmentUserSkillScores: ({
    domain = getDomain(),
    assessmentId,
  }: {
    domain?: string;
    assessmentId: number;
  }): string => path.join(
    base({ domain }),
    `assessments/${assessmentId}/user-skill-scores/`,
  ),
  assessmentUserSkillScoresFile: ({
    domain = getDomain(),
    assessmentId,
  }: {
    domain?: string;
    assessmentId: number;
  }): string => path.join(
    base({ domain }),
    `assessments/${assessmentId}/user-skill-scores/file/`,
  ),
  assessmentUserSkillScoresTemplateFile: ({
    domain = getDomain(),
  }: {
    domain?: string;
  }): string => path.join(
    base({ domain }),
    'files/assessment/template/user-skill-scores/download/',
  ),
  currentAssessmentAdmin: ({
    domain = getDomain(),
  }: {
    domain?: string;
  }): string => path.join(
    base({ domain }),
    'assessment-admins/current/',
  ),
});

const reviewUrls = Object.freeze({
  backlogReviewees: ({
    domain = getDomain(),
  }): string => path.join(
    base({ domain }),
    'reviews/backlog/reviewees/',
  ),
  newReview: ({
    domain = getDomain(),
  }: {
    domain: string;
  }): string => path.join(
    base({ domain }),
    'reviews/new/',
  ),
  reviewAttribute: ({
    domain = getDomain(),
    reviewCode,
  }: {
    domain?: string;
    reviewCode: string;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/attribute/`,
  ),
  reviewClose: ({
    domain = getDomain(),
    reviewCode,
  }: {
    domain?: string;
    reviewCode: string;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/close/`,
  ),
  reviewDetail: ({
    domain = getDomain(),
    reviewCode,
  }: {
    domain?: string;
    reviewCode: string;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/detail/`,
  ),
  reviewEditors: ({
    domain = getDomain(),
    reviewCode,
    reviewEditorId = null,
  }: {
    domain?: string;
    reviewCode: string;
    reviewEditorId?: number | null;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/editors/`,
    reviewEditorId !== null ? `${reviewEditorId}/` : '',
  ),
  reviewEditorBatchUpdate: ({
    domain = getDomain(),
    reviewCode,
  }: {
    domain?: string;
    reviewCode: string;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/editors/update/`,
  ),
  reviewees: ({
    domain = getDomain(),
    reviewCode,
    revieweeId = null,
  }: {
    domain?: string;
    reviewCode: string;
    revieweeId?: number | null;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/reviewees/`,
    revieweeId !== null ? `${revieweeId}/` : '',
  ),
  revieweeSkillActions: ({
    domain = getDomain(),
    reviewCode,
    revieweeId,
    reviewSkillId,
    revieweeSkillActionId = null,
  }: {
    domain?: string;
    reviewCode: string;
    revieweeId: number;
    reviewSkillId: number;
    revieweeSkillActionId?: number | null;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/reviewees/${revieweeId}/`,
    `skills/${reviewSkillId}/actions/`,
    revieweeSkillActionId !== null ? `${revieweeSkillActionId}/` : '',
  ),
  revieweeStates: ({
    domain = getDomain(),
    reviewCode,
    revieweeId = null,
  }: {
    domain?: string;
    reviewCode: string;
    revieweeId?: number | null;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/reviewees/`,
    revieweeId !== null ? `${revieweeId}/state/` : 'states/',
  ),
  reviewers: ({
    domain = getDomain(),
    reviewCode,
    reviewerId = null,
  }: {
    domain?: string;
    reviewCode: string;
    reviewerId?: number | null;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/reviewers/`,
    reviewerId !== null ? `${reviewerId}/` : '',
  ),
  reviewPublish: ({
    domain = getDomain(),
    reviewCode,
  }: {
    domain?: string;
    reviewCode: string;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/publish/`,
  ),
  reviewResultRevieweeSkillActions: ({
    domain = getDomain(),
    reviewCode,
    revieweeId,
    reviewSkillId,
  }: {
    domain?: string;
    reviewCode: string;
    revieweeId: number;
    reviewSkillId: number;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/results/reviewees/${revieweeId}/`,
    `skills/${reviewSkillId}/actions/`,
  ),
  reviewResultReviewSkillReviewSkillActions: ({
    domain = getDomain(),
    reviewCode,
    reviewSkillId,
  }: {
    domain?: string;
    reviewCode: string;
    reviewSkillId: number;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/results/skills/${reviewSkillId}/actions/`,
  ),
  reviewResultSkillCoverages: ({
    domain = getDomain(),
    reviewCode,
  }: {
    domain?: string;
    reviewCode: string;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/results/coverages/`,
  ),
  reviews: ({
    domain = getDomain(),
    reviewCode = null,
  }: {
    domain?: string;
    reviewCode?: string | null;
  } = {}): string => path.join(
    base({ domain }),
    'reviews/',
    reviewCode !== null ? `${reviewCode}/` : '',
  ),
  reviewSkillActions: ({
    domain = getDomain(),
    reviewCode,
    reviewSkillActionId = null,
  }: {
    domain?: string;
    reviewCode: string;
    reviewSkillActionId?: number | null;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/skill-actions/`,
    reviewSkillActionId !== null ? `${reviewSkillActionId}/` : '',
  ),
  reviewSkillMetrics: ({
    domain = getDomain(),
    reviewCode,
    reviewSkillId,
  }: {
    domain?: string;
    reviewCode: string;
    reviewSkillId: number;
  }): string => path.join(
    base({ domain }),
    `reviews/${reviewCode}/skills/${reviewSkillId}/metrics/`,
  ),
  reviewSkillsFile: ({
    domain = getDomain(),
    reviewCode,
  }: {
    domain?: string;
    reviewCode: string;
  }): string => path.join(
    base({ domain }),
    `files/reviews/${reviewCode}/skills/download/`,
  ),
  reviewTemplateFile: ({
    domain = getDomain(),
  }: {
    domain: string;
  }): string => path.join(
    base({ domain }),
    'files/review/template/download/',
  ),
});

const skillTreeUrls = Object.freeze({
  newSkillTree: ({
    domain = getDomain(),
  }: {
    domain: string;
  }): string => path.join(
    base({ domain }),
    'skill-trees/new/',
  ),
  skillTreeClose: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/close/`,
  ),
  skillTreeAttribute: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/attribute/`,
  ),
  skillTreeEditors: ({
    domain = getDomain(),
    skillTreeCode,
    skillTreeEditorId = null,
  }: {
    domain: string;
    skillTreeCode: string;
    skillTreeEditorId: number | null;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/editors/`,
    skillTreeEditorId !== null ? `${skillTreeEditorId}/` : '',
  ),
  skillTreeEditorsBatchUpdate: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/editors/update/`,
  ),
  skillTreePublish: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/publish/`,
  ),
  skillTrees: ({
    domain = getDomain(),
    skillTreeCode = null,
  }: {
    domain: string;
    skillTreeCode?: string | null;
  }): string => path.join(
    base({ domain }),
    'skill-trees/',
    skillTreeCode !== null ? `${skillTreeCode}/` : '',
  ),
  skillTreeSkillsFile: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `files/skill-trees/${skillTreeCode}/skills/download/`,
  ),
  skillTreeSummaryFile: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `files/skill-trees/${skillTreeCode}/summary/download/`,
  ),
  skillTreeTemplateFile: ({
    domain = getDomain(),
  }: {
    domain: string;
  }): string => path.join(
    base({ domain }),
    'files/skill-trees/template/download/',
  ),
  skillTreeTenantUserStateComplete: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/user/complete/`,
  ),
  skillTreeTenantUserStateReset: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/user/reset/`,
  ),
  skillTreeTenantUserStateSkip: ({
    domain = getDomain(),
    skillTreeCode,
  }: {
    domain: string;
    skillTreeCode: string;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/user/skip/`,
  ),
  skillTreeTenantUserStates: ({
    domain = getDomain(),
    skillTreeCode,
    skillTreeTenantUserStateId = null,
  }: {
    domain: string;
    skillTreeCode: string;
    skillTreeTenantUserStateId: number | null;
  }): string => path.join(
    base({ domain }),
    `skill-trees/${skillTreeCode}/users/states/`,
    skillTreeTenantUserStateId !== null ? `${skillTreeTenantUserStateId}/` : '',
  ),
});

const teamsUrls = Object.freeze({
  newTeam: ({
    domain = getDomain(),
  }: {
    domain?: string;
  }): string => path.join(
    base({ domain }),
    'teams/new/',
  ),
  teamEditors: ({
    domain = getDomain(),
    teamCode,
  }: {
    domain?: string;
    teamCode: string;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/editors/`,
  ),
  teamEditorBatchUpdate: ({
    domain = getDomain(),
    teamCode,
  }: {
    domain?: string;
    teamCode: string;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/editors/update/`,
  ),
  teamMemberRoles: ({
    domain = getDomain(),
    teamMemberRoleId = null,
  }: {
    domain?: string;
    teamMemberRoleId?: number | null;
  }): string => path.join(
    base({ domain }),
    'teams/member-roles/',
    teamMemberRoleId !== null ? `${teamMemberRoleId}/` : '',
  ),
  teamRequiredSkillAssigneeDetails: ({
    domain = getDomain(),
    teamCode,
    teamRequiredSkillId,
  }: {
    domain?: string;
    teamCode: string;
    teamRequiredSkillId: number;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/required-skills/${teamRequiredSkillId}`,
    '/assignees/all/details/',
  ),
  teamRequiredSkillAssignees: ({
    domain = getDomain(),
    teamCode,
    teamRequiredSkillId,
    teamRequiredSkillAssigneeId = null,
  }: {
    domain?: string;
    teamCode: string;
    teamRequiredSkillId: number;
    teamRequiredSkillAssigneeId?: number | null;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/required-skills/${teamRequiredSkillId}/assignees/`,
    teamRequiredSkillAssigneeId !== null ? `${teamRequiredSkillAssigneeId}/` : '',
  ),
  teamRequiredSkills: ({
    domain = getDomain(),
    teamCode,
    teamRequiredSkillId = null,
  }: {
    domain?: string;
    teamCode: string;
    teamRequiredSkillId?: number | null;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/required-skills/`,
    teamRequiredSkillId !== null ? `${teamRequiredSkillId}/` : '',
  ),
  TeamRequiredSkillsAssignees: ({
    domain = getDomain(),
    teamCode,
  }: {
    domain?: string;
    teamCode: string;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/required-skills/all/assignees/`,
  ),
  teamRequiredSkillsUserScores: ({
    domain = getDomain(),
    teamCode,
  }: {
    domain?: string;
    teamCode: string;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/required-skills/all/user-scores/`,
  ),
  teamRequiredSkillUserScores: ({
    domain = getDomain(),
    teamCode,
    teamRequiredSkillId,
  }: {
    domain?: string;
    teamCode: string;
    teamRequiredSkillId: number;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/required-skills/${teamRequiredSkillId}/`,
    'user-scores/',
  ),
  teams: ({
    domain = getDomain(),
    teamCode = null,
  }: {
    domain?: string;
    teamCode?: string | null;
  }): string => path.join(
    base({ domain }),
    'teams/',
    teamCode !== null ? `${teamCode}/` : '',
  ),
  teamTagAutocomplete: ({
    domain = getDomain(),
  }: {
    domain?: string;
  }): string => path.join(
    base({ domain }),
    'teams/tags/autocomplete/',
  ),
  teamTags: ({
    domain = getDomain(),
    teamCode,
    teamTagId = null,
  }: {
    domain?: string;
    teamCode: string;
    teamTagId?: number | null;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/tags/`,
    teamTagId !== null ? `${teamTagId}/` : '',
  ),
  teamTenantUserRequiredSkillsScores: ({
    domain = getDomain(),
    teamCode,
    teamTenantUserId,
  }: {
    domain?: string;
    teamCode: string;
    teamTenantUserId: number;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/users/${teamTenantUserId}/required-skills/all/scores/`,
  ),
  teamTenantUsers: ({
    domain = getDomain(),
    teamCode,
    teamTenantUserId = null,
  }: {
    domain?: string;
    teamCode: string;
    teamTenantUserId?: number | null;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/users/`,
    teamTenantUserId !== null ? `${teamTenantUserId}/` : '',
  ),
  teamVisibility: ({
    domain = getDomain(),
    teamCode,
  }: {
    domain?: string;
    teamCode: string;
  }): string => path.join(
    base({ domain }),
    `teams/${teamCode}/visibility/`,
  ),
});

const urls = Object.freeze({
  tenant: ({ domain = getDomain() } = {}): string => path.join(
    base({ domain }),
    '',
  ),
  newTenant: (): string => path.join(
    base({ domain: null }),
    'new/',
  ),
  searchAssessedTenantUsers: ({
    domain = getDomain(),
  }: {
    domain?: string;
  }): string => path.join(
    base({ domain }),
    'users/search/assessed/',
  ),
  searchReviewedTenantUsers: ({
    domain = getDomain(),
  }: {
    domain?: string;
  }): string => path.join(
    base({ domain }),
    'users/search/reviewed/',
  ),
  searchTenantUsers: ({ domain = getDomain() }): string => path.join(
    base({ domain }),
    'users/search/',
  ),
  skillActions: ({ domain = getDomain() }): string => path.join(
    base({ domain }),
    'skill-actions/',
  ),
  tenantAccount: ({ domain = getDomain() } = {}): string => path.join(
    base({ domain }),
    'account/',
  ),
  tenantDiagnose: ({ domain = getDomain() } = {}): string => path.join(
    base({ domain }),
    'diagnose/',
  ),
  tenantInvitationCodes: ({
    domain = getDomain(),
    tenantInvitationCodeId = null,
  }: {
    domain?: DomainType;
    tenantInvitationCodeId?: string | null;
  } = {}): string => path.join(
    base({ domain }),
    'invitation-codes/',
    tenantInvitationCodeId !== null ? `${tenantInvitationCodeId}/` : '',
  ),
  tenantShares: (): string => path.join(
    base({ domain: null }),
    'shares/',
  ),
  tenantSkillAutocomplete: ({ domain = getDomain() } = {}): string => path.join(
    base({ domain }),
    'skills/autocomplete/',
  ),
  tenantSkillsGraph: ({ domain = getDomain() } = {}): string => path.join(
    base({ domain }),
    'skills/graph/',
  ),
  tenantParams: ({ domain = getDomain() } = {}): string => path.join(
    base({ domain }),
    'params/',
  ),
  tenantUserAutocomplete: ({ domain = getDomain() } = {}): string => path.join(
    base({ domain }),
    'users/autocomplete/',
  ),
  tenantUserRole: ({
    domain = getDomain(),
    tenantUserId,
  }): string => path.join(
    base({ domain }),
    `users/${tenantUserId}/role/`,
  ),
  ...agentUrls,
  ...assessmentUrls,
  ...reviewUrls,
  ...skillTreeUrls,
  ...teamsUrls,
});

export default urls;
